import React, { useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { Order } from '../services/api';

interface OrderSymbolFilterProps {
  orders: Order[];
  value: string;
  onChange: (value: string) => void;
}

export const matchesOrderSymbol = (order: Order, filter: string) => {
  const target = filter.trim().toUpperCase();
  if (!target) return true;

  return [
    order.symbol,
    order.rootSymbol,
    order.localSymbol,
    order.lastTradeDateOrContractMonth,
  ].some((value) => String(value || '').toUpperCase().includes(target));
};

const OrderSymbolFilter: React.FC<OrderSymbolFilterProps> = ({ orders, value, onChange }) => {
  const symbols = useMemo(() => Array.from(new Set(
    orders
      .map((order) => String(order.symbol || order.rootSymbol || order.localSymbol || '').trim().toUpperCase())
      .filter(Boolean),
  )).sort(), [orders]);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/2.5 px-3 py-2.5">
      <span className="mr-1 text-[10px] font-black uppercase tracking-widest text-white/40">Quick symbol</span>
      <button
        type="button"
        onClick={() => onChange('')}
        className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${!value
          ? 'border-neon/30 bg-neon/10 text-neon'
          : 'border-white/10 text-white/50 hover:border-white/20 hover:text-white'
          }`}
      >
        All
      </button>
      {symbols.map((symbol) => (
        <button
          key={symbol}
          type="button"
          onClick={() => onChange(symbol)}
          className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold tracking-wider transition-colors ${value.toUpperCase() === symbol
            ? 'border-neon/30 bg-neon/10 text-neon'
            : 'border-white/10 text-white/50 hover:border-neon/25 hover:text-neon'
            }`}
        >
          {symbol}
        </button>
      ))}
      <label className="relative ml-auto min-w-[180px] flex-1 sm:max-w-[240px]">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Filter symbol…"
          aria-label="Filter orders by symbol"
          className="h-8 w-full rounded-lg border border-white/10 bg-black/20 pl-8 pr-8 text-xs text-white outline-hidden placeholder:text-white/30 focus:border-neon/40"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-white/40 hover:bg-white/10 hover:text-white"
            aria-label="Clear symbol filter"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </label>
    </div>
  );
};

export default OrderSymbolFilter;
